<template>
    <div class="community-profile-container">
        <!-- Main Profile Card -->
        <div class="profile-card">
            <div class="profile-header">
                <!-- Profile Picture and Basic Info -->
                <div class="profile-info">
                    <ProfilePicture 
                        :mediaPath="communityProfile ? communityProfile.profile_picture : null"
                        :fullName="fullName"
                        size="large"
                        :altText="`Photo de profil de ${fullName}`"
                        :editable="false"
                        :profileId="communityProfile?.id || 0"
                    />
                    <div class="profile-details">
                        <h2 class="profile-name">
                            {{ fullName }}
                        </h2>
                    </div>
                </div>

                <!-- Infos Section -->
                <div class="infos-section">
                    <div class="info-item">
                        <label>Niveaux</label>
                        <div class="tags">
                            <span 
                                v-for="level in levelsToShow" 
                                :key="level.id || level"
                                class="tag tile level"
                            >
                                {{ getLevel(level) }}
                            </span>
                        </div>
                    </div>
                    <div class="info-item">
                        <label>Matières</label>
                        <span class="subjects">{{ formattedSubjects }}</span>
                    </div>
                </div>
            </div>

            <!-- Additional Details -->
            <div class="profile-details-grid">
                <div class="detail-item">
                    <label>Département</label>
                    <span>{{ displayCounty }}</span>
                </div>
                <div class="detail-item">
                    <label>Ville</label>
                    <span>{{ displayCity }}</span>
                </div>
                <div class="detail-item">
                    <label>Établissement scolaire</label>
                    <span>{{ displaySchool }}</span>
                </div>
                <div class="detail-item">
                    <label>Dernier diplôme obtenu</label>
                    <span>{{ displayDiploma }}</span>
                </div>
            </div>
        </div>

        <!-- Shared Files Section -->
        <div class="shared-files-section">
            <SharedFilesTable 
                :sharedFiles="sharedFiles" 
                :totalCount="totalCount"
                :currentPage="currentPage"
                :totalPages="totalPages"
                :loading="sharedFilesLoading"
                :showSearch="true"
                :showFolders="true"
                :sharedFolders="sharedFolders"
                :breadcrumb="breadcrumb"
                @open-folder="handleOpenFolder"
                @go-root="goToRoot"
                @go-breadcrumb="goToBreadcrumb"
                @filters-changed="handleFiltersChanged"
                @page-changed="handleSharedFilesPageChange"
            />
        </div>
    </div>
</template>

<script>
import { getPublicCommunityProfileByIdService } from '@/services/communityProfileService'
import SharedFilesTable from '../Resources/Resource/ShareSpace/SharedFilesTable.vue'
import ProfilePicture from './ProfilePicture.vue'
import { sharedFilesByIdService, sharedFoldersByIdService } from '../../../services/filesService'
import { getFolderService } from '../../../services/foldersService'

export default {
    name: 'PublicCommunityProfile',
    components: {
        SharedFilesTable,
        ProfilePicture
    },
    data() {
        return {
            loading: true,
            error: null,
            communityProfile: null,
            sharedFiles: [],
            sharedFolders: [],
            totalCount: 0,
            currentPage: 0,
            totalPages: 0,
            sharedFilesLoading: false,
            itemsPerPage: 10,
            // navigation
            currentFolderId: null,
            breadcrumb: [],
            // filtres
            filters: {}
        }
    },
    computed: {
        fullName() {
            return this.communityProfile?.full_name || this.communityProfile?.display_name || 'Profil public'
        },
        formattedSubjects() {
            if (this.communityProfile && Array.isArray(this.communityProfile.subjects) && this.communityProfile.subjects.length > 0) {
                return this.communityProfile.subjects.map(s => s.name || s).join(', ')
            }
            return 'Aucune matière'
        }
        ,
        levelsToShow() {
            // Prefer levels from the public community profile (array of objects {id,name})
            if (this.communityProfile && Array.isArray(this.communityProfile.levels) && this.communityProfile.levels.length > 0) {
                return this.communityProfile.levels
            }
            // Fallback to store-level ids
            return this.$store.state.user.levels || []
        }
        ,
        displayCounty() {
            return this.communityProfile?.profile?.county_name || this.communityProfile?.city || '—'
        },
        displayCity() {
            return this.communityProfile?.profile?.city || this.communityProfile?.location || '—'
        },
        displaySchool() {
            return this.communityProfile?.profile?.school || 'Aucun'
        },
        displayDiploma() {
            return this.communityProfile?.profile?.diploma || 'Non renseigné'
        }
    },
    methods: {
        resolveTeacherUserId() {
            // Utilise directement user_id renvoyé par l'API publique si présent
            if (this.communityProfile && this.communityProfile.user_id) {
                return this.communityProfile.user_id
            }
            const teacherId = this.communityProfile?.profile?.user || this.communityProfile?.teacher_id || this.communityProfile?.profile_id
            if (teacherId && typeof teacherId === 'object') {
                return teacherId.id || teacherId.user || teacherId.user_id || null
            }
            return teacherId || null
        },
        getLevel(level) {
            // If level is an object with a name, return it
            if (level && typeof level === 'object') {
                return level.name || level
            }
            // Otherwise try to resolve id against store levels
            if (this.$store.state.levels) {
                const found = this.$store.state.levels.find(x => x.id === level)
                return found?.name || level
            }
            return level
        },
        async loadSharedFilesByTeacher(teacherId, page = 1) {
            try {
                this.sharedFilesLoading = true
                if (!teacherId) {
                    this.sharedFiles = []
                    this.totalCount = 0
                    this.totalPages = 0
                    return
                }

                const response = await sharedFilesByIdService(teacherId, page, this.itemsPerPage, this.currentFolderId, this.filters, 'user_id')
                if (response && response.results) {
                    this.sharedFiles = response.results
                    this.totalCount = response.count
                    this.totalPages = Math.ceil(response.count / this.itemsPerPage)
                } else {
                    this.sharedFiles = Array.isArray(response) ? response : []
                    this.totalCount = this.sharedFiles.length
                    this.totalPages = 1
                }
                // dossiers
                const folderResp = await sharedFoldersByIdService(teacherId, page, this.itemsPerPage, this.currentFolderId, this.filters, 'user_id')
                if (folderResp && folderResp.results) {
                    if (!this.currentFolderId) this.sharedFolders = folderResp.results.filter(f => f.parent_folder_id === null)
                    else this.sharedFolders = folderResp.results
                } else {
                    const raw = Array.isArray(folderResp) ? folderResp : []
                    this.sharedFolders = !this.currentFolderId ? raw.filter(f => f.parent_folder_id === null) : raw
                }
            } catch (error) {
                console.error('Erreur lors du chargement des fichiers partagés:', error)
                this.sharedFiles = []
                this.sharedFolders = []
                this.totalCount = 0
                this.totalPages = 0
            } finally {
                this.sharedFilesLoading = false
            }
        },
        handleSharedFilesPageChange(page) {
            this.currentPage = page
            // API pages are 1-based
            const apiPage = page + 1
            const teacherUserId = this.resolveTeacherUserId()
            this.loadSharedFilesByTeacher(teacherUserId, apiPage)
        },
        handleFiltersChanged(f) {
            this.filters = f || {}
            this.currentPage = 0
            const teacherUserId = this.resolveTeacherUserId()
            this.loadSharedFilesByTeacher(teacherUserId, 1)
        },
        async buildBreadcrumbFromFolder(folderId) {
            this.breadcrumb = []
            if (!folderId) return
            const chain = []
            let currentId = folderId
            for (let i = 0; i < 50 && currentId; i++) {
                const folder = await getFolderService(currentId)
                if (!folder) break
                chain.push({ id: folder.id, name: folder.name, parent_folder_id: folder.parent_folder_id })
                currentId = folder.parent_folder_id
            }
            chain.reverse()
            this.breadcrumb = chain
        },
        handleOpenFolder(folder) {
            if (!folder || !folder.id) return
            // navigation par route publique
            this.$router.push({ name: 'CommunityProfilePublic', params: { id: this.$route.params.id }, query: { folderId: folder.id } })
        },
        goToRoot() {
            this.$router.push({ name: 'CommunityProfilePublic', params: { id: this.$route.params.id } })
        },
        goToBreadcrumb(index) {
            const target = this.breadcrumb[index]
            if (!target) return this.goToRoot()
            this.$router.push({ name: 'CommunityProfilePublic', params: { id: this.$route.params.id }, query: { folderId: target.id } })
        },
        handleProfilePictureUpload(data) {
            if (this.communityProfile) {
                this.communityProfile.profile_picture = data.result || data
            }
            this.$store.state.toast = { status: 'success', text: 'Photo de profil mise à jour' }
        },
        handleProfilePictureError(error) {
            this.$store.state.toast = { status: 'error', text: error?.message || 'Erreur lors de l\'upload' }
        }
    },
    async created() {
        try {
            const id = this.$route && this.$route.params ? this.$route.params.id : null
            if (!id) {
                this.error = 'Aucun id fourni dans l\'URL'
                this.loading = false
                return
            }

            const data = await getPublicCommunityProfileByIdService(id)
            this.communityProfile = data
            const teacherId = this.resolveTeacherUserId()

            const folderId = this.$route?.query?.folderId ? parseInt(this.$route.query.folderId) : null
            this.currentFolderId = isNaN(folderId) ? null : folderId
            await this.buildBreadcrumbFromFolder(this.currentFolderId)

            await this.loadSharedFilesByTeacher(teacherId, 1)
        } catch (err) {
            this.error = err?.message || 'Erreur lors du chargement du profil'
            console.error('CommunityProfilePublic - erreur:', err)
        } finally {
            this.loading = false
        }
    },
    watch: {
        '$route.query.folderId': {
            immediate: false,
            async handler(newVal) {
                const id = newVal ? parseInt(newVal) : null
                this.currentFolderId = isNaN(id) ? null : id
                await this.buildBreadcrumbFromFolder(this.currentFolderId)
                this.currentPage = 0
                const teacherUserId = this.resolveTeacherUserId()
                await this.loadSharedFilesByTeacher(teacherUserId, 1)
            }
        }
    }
}
</script>

<style scoped lang="scss">
@import '../../../assets/scss/_variable.scss';

/* Profile Card */
.profile-card {
    background-color: white;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
}

.profile-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 30px;
}

.profile-info {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-right: 20px;
}

.profile-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.profile-name {
    @include sh2;
    color: #2d3748;
    margin: 0;
}

.location {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #718096;
    @include b2;
}

.location-icon {
    font-size: 1rem;
}

/* Infos Section */
.infos-section {
    background-color: $light-light-blue;
    padding: 20px;
    border-radius: 12px;
    min-width: 250px;
}

.infos-section h3 {
    margin: 0 0 15px 0;
    @include sh2;
    color: #2d3748;
}

.info-item {
    margin-bottom: 12px;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-item label {
    display: block;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 4px;
    @include sh2;
}

.tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.tag {
    background-color: #e2e8f0;
    color: #4a5568;
    padding: 3px 10px;
    border-radius: 16px;
    @include s2;
    font-weight: 500;
}

.subjects {
    color: #3182ce;
    font-weight: 500;
    @include b2;
}

.experience {
    color: #2d3748;
    font-weight: 500;
    @include b2;
}

/* Profile Details Grid */
.profile-details-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.detail-item label {
    font-weight: 600;
    color: #4a5568;
    @include sh2;
}

.detail-item span {
    color: #2d3748;
    font-weight: 500;
    @include b2;
}

/* Shared Files Section */
.shared-files-section {
    margin-top: 25px;
}

/* Create Profile Section */
.create-profile-section {
    background-color: #f7fafc;
    padding: 25px;
    border-radius: 12px;
    text-align: center;
    margin-top: 25px;
}

.create-profile-section h2 {
    margin: 0 0 12px 0;
    color: #2d3748;
    @include sh2;
}

.create-profile-section p {
    color: #718096;
    margin-bottom: 20px;
    @include b2;
}

.create-button {
    background-color: #553c9a;
    color: white;
    border: none;
    padding: 10px 25px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    @include bt2;
    transition: background-color 0.2s;
}

.create-button:hover:not(:disabled) {
    background-color: #44337a;
}

.create-button:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
}

.error-message {
    color: #e53e3e;
    margin-top: 12px;
    padding: 10px;
    background-color: #fed7d7;
    border: 1px solid #feb2b2;
    border-radius: 8px;
    @include b2;
}

.success-message {
    color: #22543d;
    margin-top: 12px;
    padding: 10px;
    background-color: #c6f6d5;
    border: 1px solid #9ae6b4;
    border-radius: 8px;
    @include b2;
}

/* Responsive Design */
@media (max-width: 768px) {
    .profile-header {
        flex-direction: column;
        gap: 20px;
    }
    
    .profile-details-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .profile-details-grid {
        grid-template-columns: 1fr;
    }
    
    .profile-info {
        flex-direction: column;
        text-align: center;
    }
}
</style>